import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { QRCodeSVG as QRCode } from 'qrcode.react';
import fetchData from '../../functions/fetchData';
import './CashierTicketBilling.css';

export default function BillsManagement() {
  const navigate = useNavigate();
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(false);
  const [billDetails, setBillDetails] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [currentBillForPrint, setCurrentBillForPrint] = useState(null);
  const [filters, setFilters] = useState({
    start_date: new Date().toISOString().split('T')[0], // Today
    end_date: new Date().toISOString().split('T')[0],   // Today
    payment_method: '',
    status: '', // all, active, cancelled
    page: 1,
    limit: 10 // Changed to 10 records per page
  });
  const [pagination, setPagination] = useState({
    total_count: 0,
    total_pages: 0,
    current_page: 1
  });
  const [showQRModal, setShowQRModal] = useState(false);
  const [qrData, setQrData] = useState(null);
  const [selectedBillForQR, setSelectedBillForQR] = useState(null);

  // Generate QR code as a data URL
  const generateQRCodeDataUrl = async (text) => {
    try {
      const value = String(text || '').trim();
      if (!value) return '';

      const QRCode = require('qrcode');
      return await QRCode.toDataURL(value, {
        width: 256,
        margin: 1,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });
    } catch (error) {
      console.error('Error generating QR code:', error);
      return '';
    }
  };

  // Show QR modal for bill
  const showBillQRModal = (bill) => {
    setSelectedBillForQR(bill);
    setQrData({
      billNumber: bill.bill_number,
      amount: bill.total_amount,
      customerName: bill.customer_name,
      createdAt: bill.created_at
    });
    setShowQRModal(true);
  };

  // Close QR modal
  const closeQRModal = () => {
    setShowQRModal(false);
    setQrData(null);
    setSelectedBillForQR(null);
  };

  // ========== ESC/POS THERMAL PRINTING FUNCTIONS ==========

  const loadImageFromDataUrl = (dataUrl) => new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = dataUrl;
  });

  // Generate canvas-based ESC/POS raster data for thermal printer
  const generateTicketCanvas = async (ticketData) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    const dotsPerMM = 8;
    const contentWidth = 550;
    const contentHeight = 80 * dotsPerMM;

    canvas.width = contentHeight; // 640 pixels (80mm)
    canvas.height = contentWidth;

    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.save();
    ctx.translate(canvas.width, 0);
    ctx.rotate(Math.PI / 2);

    ctx.fillStyle = 'black';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';

    const col1X      = 33 * dotsPerMM;
    const col2X      = 55 * dotsPerMM;
    let yPos         = 20 * dotsPerMM;
    const labelGap   = 24;
    const lineHeight = 40;

    const currentDate = new Date(ticketData.timestamp);
    const formattedDateTime = `${String(currentDate.getDate()).padStart(2,'0')}/${String(currentDate.getMonth()+1).padStart(2,'0')} ${currentDate.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false })}`;

    // ROW 1: TICKET ID | DATE
    ctx.font = 'bold 20px Arial';
    ctx.fillText('TICKET ID', col1X, yPos);
    ctx.fillText('DATE', col2X, yPos);
    yPos += labelGap;
    ctx.font = '20px Arial';
    ctx.fillText(ticketData.ticketId || 'N/A', col1X, yPos);
    ctx.fillText(formattedDateTime, col2X, yPos);
    yPos += lineHeight;

    // ROW 2+: ITEM × QTY | PRICE
    if (ticketData.items && ticketData.items.length > 0) {
      ticketData.items.forEach(item => {
        const itemTotal = typeof item.total === 'number' ? item.total : (parseFloat(item.price || 0) * item.quantity);
        ctx.font = 'bold 20px Arial';
        ctx.fillText(`${item.item_name || item.name} X ${item.quantity}`, col1X, yPos);
        ctx.fillText(`฿${itemTotal}`, col2X, yPos);
        yPos += lineHeight;
      });
    }

    // ROW LAST: TOTAL AMOUNT | VALUE
    ctx.font = 'bold 20px Arial';
    ctx.fillText('TOTAL AMOUNT', col1X, yPos);
    ctx.fillText(`฿${ticketData.total}`, col2X, yPos);

    // QR CODE — drawn below TOTAL AMOUNT
    const verifyValue = ticketData?.barcodeValue || ticketData?.ticketId || ticketData?.bill_number || '';
    if (verifyValue) {
      yPos += lineHeight;
      const qrSize = 170;
      try {
        const qrDataUrl = await generateQRCodeDataUrl(verifyValue);
        if (qrDataUrl) {
          const qrImage = await loadImageFromDataUrl(qrDataUrl);
          ctx.drawImage(qrImage, col1X, yPos, qrSize, qrSize);
        }
      } catch (qrError) {
        console.error('Failed to render QR in ESC ticket canvas:', qrError);
      }
    }

    ctx.restore();

    return canvas;
  };

  // Convert canvas to ESC/POS commands
  const canvasToESCPOS = (canvas) => {
    const dotsPerMM = 8;
    const imageData = canvas.getContext('2d').getImageData(0, 0, canvas.width, canvas.height);
    const pixels = imageData.data;
    
    const monoPixels = [];
    for (let i = 0; i < pixels.length; i += 4) {
      const gray = (pixels[i] + pixels[i + 1] + pixels[i + 2]) / 3;
      monoPixels.push(gray > 128 ? 0 : 1);
    }
    
    const rasterData = [];
    for (let y = 0; y < canvas.height; y++) {
      const rowBytes = [];
      for (let x = 0; x < canvas.width; x += 8) {
        let byte = 0;
        for (let bit = 0; bit < 8; bit++) {
          if (x + bit < canvas.width) {
            const pixelIndex = y * canvas.width + (x + bit);
            if (monoPixels[pixelIndex]) {
              byte |= (0x80 >> bit);
            }
          }
        }
        rowBytes.push(byte);
      }
      rasterData.push(...rowBytes);
    }
    
    const commands = [];
    commands.push(0x1B, 0x40); // Initialize
    
    // Add 33mm blank space before printing
    const feedAmount = Math.floor(33 * dotsPerMM / 8);
    if (feedAmount > 255) {
      const fullFeeds = Math.floor(feedAmount / 255);
      const remainder = feedAmount % 255;
      for (let i = 0; i < fullFeeds; i++) {
        commands.push(0x1B, 0x64, 255);
      }
      if (remainder > 0) {
        commands.push(0x1B, 0x64, remainder);
      }
    } else {
      commands.push(0x1B, 0x64, feedAmount);
    }
    
    // Send raster image
    const widthBytes = Math.ceil(canvas.width / 8);
    const widthL = widthBytes & 0xFF;
    const widthH = (widthBytes >> 8) & 0xFF;
    const heightL = canvas.height & 0xFF;
    const heightH = (canvas.height >> 8) & 0xFF;
    
    commands.push(0x1D, 0x76, 0x30, 0x00);
    commands.push(widthL, widthH);
    commands.push(heightL, heightH);
    commands.push(...rasterData);
    
    // Feed and cut
    commands.push(0x1B, 0x64, 0x03);
    commands.push(0x1D, 0x56, 0x42, 0x00);
    
    return new Uint8Array(commands);
  };

  // Send ESC/POS data to thermal printer
  const sendToThermalPrinter = async (escposData) => {
    try {
      const base64 = btoa(String.fromCharCode.apply(null, escposData));
      const response = await fetch('http://localhost:6001/print', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ data: base64 }),
      });
      
      if (!response.ok) {
        throw new Error('Printer server error');
      }
      
      const result = await response.json();
      console.log('Print result:', result);
      return true;
    } catch (error) {
      console.error('Failed to send to thermal printer:', error);
      toast.error('Failed to send to thermal printer. Make sure printer server is running on port 5001.', {
        position: "top-right",
        autoClose: 5000,
      });
      return false;
    }
  };

  // Print multiple tickets using ESC/POS thermal printer
  const printMultipleTickets = async (ticketsArray) => {
    try {
      toast.info(`Sending ${ticketsArray.length} ticket(s) to thermal printer...`, {
        position: "top-right",
        autoClose: 2000,
      });
      
      for (const ticketData of ticketsArray) {
        const canvas = await generateTicketCanvas(ticketData);
        const escposData = canvasToESCPOS(canvas);
        await sendToThermalPrinter(escposData);
        // Small delay between tickets
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      
      toast.success(`Successfully sent ${ticketsArray.length} ticket(s) to thermal printer!`, {
        position: "top-right",
        autoClose: 3000,
      });
    } catch (error) {
      console.error('Error printing tickets:', error);
      toast.error('Failed to print tickets. Check printer connection.', {
        position: "top-right",
        autoClose: 5000,
      });
    }
  };

  // Print single ticket using ESC/POS thermal printer
  const printTicket = async (ticketData) => {
    await printMultipleTickets([ticketData]);
  };

  // ========== HTML-BASED THERMAL PRINT FUNCTIONS (80mm width with COPY watermark) ==========
  
  // Print multiple separate tickets using HTML thermal printer (80mm width) with COPY watermark
  const printMultipleTicketsThermalHTML = async (ticketsArray) => {
    const printWindow = window.open('', '_blank');
    
    let allTicketsHtml = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Thermal Tickets - The View Pattaya (COPY)</title>
        <meta charset="UTF-8">
        <style>
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          
          body { 
            font-family: 'Courier New', monospace;
            background: white;
            padding: 0;
            margin: 0;
            width: 80mm;
          }
          
          .thermal-ticket {
            width: 80mm;
            padding: 5mm;
            background: white;
            page-break-after: always;
            page-break-inside: avoid;
            border-bottom: 2px dashed #000;
            position: relative;
          }
          
          .thermal-ticket:last-child {
            page-break-after: auto;
            border-bottom: none;
          }
          
          .watermark {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%) rotate(-45deg);
            font-size: 52px;
            font-weight: 900;
            color: rgba(255, 0, 0, 0.4);
            z-index: 1;
            pointer-events: none;
            text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.1);
            letter-spacing: 8px;
          }
          
          .ticket-content {
            position: relative;
            z-index: 2;
          }
          
          .ticket-header {
            text-align: center;
            border-bottom: 2px solid #000;
            padding-bottom: 3mm;
            margin-bottom: 3mm;
          }
          
          .ticket-title {
            font-size: 18px;
            font-weight: bold;
            margin-bottom: 2mm;
          }
          
          .ticket-subtitle {
            font-size: 12px;
            margin-bottom: 1mm;
          }
          
          .ticket-body {
            margin-bottom: 3mm;
          }
          
          .ticket-row {
            display: flex;
            justify-content: space-between;
            margin-bottom: 2mm;
            font-size: 12px;
          }
          
          .ticket-row-label {
            font-weight: bold;
          }
          
          .items-section {
            border-top: 1px dashed #000;
            border-bottom: 1px dashed #000;
            padding: 3mm 0;
            margin: 3mm 0;
          }
          
          .item-row {
            display: flex;
            justify-content: space-between;
            margin-bottom: 2mm;
            font-size: 13px;
            font-weight: 600;
          }
          
          .total-row {
            display: flex;
            justify-content: space-between;
            margin-top: 3mm;
            padding-top: 3mm;
            border-top: 2px solid #000;
            font-size: 14px;
            font-weight: bold;
          }
          
          .ticket-footer {
            text-align: center;
            font-size: 10px;
            border-top: 2px solid #000;
            padding-top: 3mm;
            margin-top: 3mm;
          }
          
          .validity-box {
            background: #f0f0f0;
            border: 1px solid #000;
            padding: 2mm;
            text-align: center;
            margin: 2mm 0;
            font-size: 11px;
            font-weight: bold;
          }
          
          .copy-label {
            text-align: center;
            color: #080808FF;
            font-weight: 900;
            font-size: 15px;
            margin: 2mm 0;
            padding: 2.5mm;
            border: 2.5px solid #080808FF;
            background: rgba(220, 53, 69, 0.05);
            text-transform: uppercase;
            letter-spacing: 1px;
          }

          .ticket-qr {
            text-align: center;
            margin: 3mm 0;
            padding: 3mm 0;
            border-top: 1px dashed #000;
            border-bottom: 1px dashed #000;
          }

          .qr-code {
            width: 30mm;
            height: 30mm;
            margin: 0 auto;
          }

          @page {
            size: 80mm auto;
            margin: 0;
          }
          
          @media print {
            * {
              -webkit-print-color-adjust: exact !important;
              color-adjust: exact !important;
            }
            
            .no-print { display: none; }
            
            body {
              width: 80mm;
            }
            
            .thermal-ticket {
              page-break-after: always !important;
              page-break-inside: avoid !important;
            }
            
            .thermal-ticket:last-child {
              page-break-after: auto !important;
            }
          }
        </style>
      </head>
      <body>
    `;

    for (const ticketData of ticketsArray) {
      const currentDate = new Date(ticketData.timestamp);
      const validTill = new Date(currentDate.getTime() + (1 * 60 * 60 * 1000));
      const qrCodeDataUrl = await generateQRCodeDataUrl(ticketData.ticketId || ticketData.bill_number || '');

      allTicketsHtml += `
        <div class="thermal-ticket">
          <div class="watermark">COPY</div>
          <div class="ticket-content">
            <div class="copy-label">*** REPRINT - COPY ***</div>
            
            <div class="ticket-header">
              <div class="ticket-title">🎡 THE VIEW PATTAYA</div>
              <div class="ticket-subtitle">TAX INVOICE/RECEIPT</div>
            </div>
            
            <div class="ticket-body">
              <div class="ticket-row">
                <span class="ticket-row-label">Bill No:</span>
                <span>${ticketData.bill_number || 'N/A'}</span>
              </div>
              <div class="ticket-row">
                <span class="ticket-row-label">Ticket ID:</span>
                <span>${ticketData.ticketId || 'N/A'}</span>
              </div>
              <div class="ticket-row">
                <span class="ticket-row-label">Date:</span>
                <span>${currentDate.toLocaleDateString('en-GB')}</span>
              </div>
              <div class="ticket-row">
                <span class="ticket-row-label">Time:</span>
                <span>${currentDate.toLocaleTimeString('en-GB')}</span>
              </div>
            </div>
            
            <div class="items-section">
              <div style="text-align: center; font-weight: bold; margin-bottom: 2mm; font-size: 12px;">ITEMS</div>
              ${ticketData.items.map(item => `
                <div class="item-row">
                  <span>${item.item_name}${item.quantity > 1 ? ' x' + item.quantity : ''}</span>
                  <span>฿${(typeof item.total === 'number' ? item.total : parseFloat(item.total) || 0).toFixed(2)}</span>
                </div>
              `).join('')}
               <div class="total-row">
                <span>VAT(7%):</span>
                <span>Included</span>
              </div>
              <div class="total-row">
                <span>TOTAL:</span>
                <span>฿${(typeof ticketData.total === 'number' ? ticketData.total : parseFloat(ticketData.total) || 0).toFixed(2)}</span>
              </div>
            </div>

            ${qrCodeDataUrl ? `
              <div class="ticket-qr">
                <img class="qr-code" src="${qrCodeDataUrl}" alt="Ticket QR Code">
              </div>
            ` : ''}

            <div class="validity-box">
              VALID UNTIL: ${validTill.toLocaleString('en-GB')}
            </div>

            <div class="copy-label">*** DUPLICATE - NOT ORIGINAL ***</div>

            <div class="ticket-footer">
              <div>Thank you for visiting!</div>
              <div style="margin-top: 2mm; font-size: 9px;">
                Cloudnet Softwares<br>
                +66-948712350 | +66-952477020
              </div>
            </div>
          </div>
        </div>
      `;
    }

    allTicketsHtml += `
        <div class="no-print" style="text-align: center; margin-top: 10mm; padding: 5mm;">
          <button onclick="window.print()" style="
            background: linear-gradient(135deg, #007bff, #0056b3);
            color: white;
            border: none;
            padding: 10px 30px;
            border-radius: 8px;
            font-size: 16px;
            font-weight: 600;
            cursor: pointer;
            box-shadow: 0 4px 15px rgba(0, 123, 255, 0.3);
          ">
            🖨️ Print Tickets (COPY)
          </button>
          <p style="margin-top: 10px; color: #666; font-size: 12px;">
            Ready to print ${ticketsArray.length} ticket(s) - REPRINT COPY
          </p>
        </div>
        <script>
          window.onafterprint = function() {
            setTimeout(function() {
              window.close();
            }, 500);
          };
        </script>
      </body>
      </html>
    `;

    printWindow.document.write(allTicketsHtml);
    printWindow.document.close();
    
    toast.success('Thermal print preview opened (COPY). Click print button.', {
      position: "top-right",
      autoClose: 3000,
    });
  };

  // Print combined ticket using HTML thermal printer (80mm width) with COPY watermark
  const printCombinedTicketThermalHTML = async (ticketData) => {
    const printWindow = window.open('', '_blank');
    const currentDate = new Date(ticketData.timestamp);
    const validTill = new Date(currentDate.getTime() + (1 * 60 * 60 * 1000));
    const qrCodeDataUrl = await generateQRCodeDataUrl(ticketData.ticketId || ticketData.bill_number || '');
    
    let ticketHtml = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Combined Thermal Ticket - The View Pattaya (COPY)</title>
        <meta charset="UTF-8">
        <style>
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          
          body { 
            font-family: 'Courier New', monospace;
            background: white;
            padding: 0;
            margin: 0;
            width: 80mm;
          }
          
          .thermal-ticket {
            width: 80mm;
            padding: 5mm;
            background: white;
            position: relative;
          }
          
          .watermark {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%) rotate(-45deg);
            font-size: 72px;
            font-weight: 900;
            color: rgba(255, 0, 0, 0.35);
            z-index: 1;
            pointer-events: none;
            text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.1);
            letter-spacing: 10px;
          }
          
          .ticket-content {
            position: relative;
            z-index: 2;
          }
          
          .ticket-header {
            text-align: center;
            border-bottom: 2px solid #000;
            padding-bottom: 3mm;
            margin-bottom: 3mm;
          }
          
          .ticket-title {
            font-size: 18px;
            font-weight: bold;
            margin-bottom: 2mm;
          }
          
          .ticket-subtitle {
            font-size: 12px;
            margin-bottom: 1mm;
          }
          
          .ticket-body {
            margin-bottom: 3mm;
          }
          
          .ticket-row {
            display: flex;
            justify-content: space-between;
            margin-bottom: 2mm;
            font-size: 12px;
          }
          
          .ticket-row-label {
            font-weight: bold;
          }
          
          .items-section {
            border-top: 1px dashed #000;
            border-bottom: 1px dashed #000;
            padding: 3mm 0;
            margin: 3mm 0;
          }
          
          .item-row {
            display: flex;
            justify-content: space-between;
            margin-bottom: 2mm;
            font-size: 13px;
            font-weight: 700;
            font-family: 'Arial', sans-serif;
          }
          
          .total-row {
            display: flex;
            justify-content: space-between;
            margin-top: 3mm;
            padding-top: 3mm;
            border-top: 2px solid #000;
            font-size: 14px;
            font-weight: bold;
          }
          
          .ticket-footer {
            text-align: center;
            font-size: 10px;
            border-top: 2px solid #000;
            padding-top: 3mm;
            margin-top: 3mm;
          }
          
          .validity-box {
            background: #f0f0f0;
            border: 1px solid #000;
            padding: 2mm;
            text-align: center;
            margin: 2mm 0;
            font-size: 11px;
            font-weight: bold;
          }
          
          .copy-label {
            text-align: center;
            color: #0E0D0DFF;
            font-weight: 900;
            font-size: 15px;
            margin: 2mm 0;
            padding: 2.5mm;
            border: 2.5px solid #0C0C0CFF;
            background: rgba(220, 53, 69, 0.05);
            text-transform: uppercase;
            letter-spacing: 1px;
          }

          .ticket-qr {
            text-align: center;
            margin: 3mm 0;
            padding: 3mm 0;
            border-top: 1px dashed #000;
            border-bottom: 1px dashed #000;
          }

          .qr-code {
            width: 30mm;
            height: 30mm;
            margin: 0 auto;
          }

          @page {
            size: 80mm auto;
            margin: 0;
          }
          
          @media print {
            * {
              -webkit-print-color-adjust: exact !important;
              color-adjust: exact !important;
            }
            
            .no-print { display: none; }
            
            body {
              width: 80mm;
            }
          }
        </style>
      </head>
      <body>
        <div class="thermal-ticket">
          <div class="watermark">COPY</div>
          <div class="ticket-content">
            <div class="copy-label">*** REPRINT - COPY ***</div>
            
            <div class="ticket-header">
              <div class="ticket-title">🎡 THE VIEW PATTAYA</div>
              <div class="ticket-subtitle">TAX INVOICE/RECEIPT</div>
            </div>
            
            <div class="ticket-body">
              <div class="ticket-row">
                <span class="ticket-row-label">Bill No:</span>
                <span>${ticketData.bill_number || 'N/A'}</span>
              </div>
              <div class="ticket-row">
                <span class="ticket-row-label">Ticket ID:</span>
                <span>${ticketData.ticketId || 'N/A'}</span>
              </div>
              <div class="ticket-row">
                <span class="ticket-row-label">Date:</span>
                <span>${currentDate.toLocaleDateString('en-GB')}</span>
              </div>
              <div class="ticket-row">
                <span class="ticket-row-label">Time:</span>
                <span>${currentDate.toLocaleTimeString('en-GB')}</span>
              </div>
            </div>
            
            <div class="items-section">
              <div style="text-align: center; font-weight: bold; margin-bottom: 2mm; font-size: 12px;">ITEMS</div>
              ${ticketData.items.map(item => `
                <div class="item-row">
                  <span>${item.item_name}${item.quantity > 1 ? ' x' + item.quantity : ''}</span>
                  <span>฿${(typeof item.total === 'number' ? item.total : parseFloat(item.total) || 0).toFixed(2)}</span>
                </div>
              `).join('')}
               <div class="total-row">
                <span>VAT(7%):</span>
                <span>Included</span>
              </div>
              <div class="total-row">
                <span>TOTAL:</span>
                <span>฿${(typeof ticketData.total === 'number' ? ticketData.total : parseFloat(ticketData.total) || 0).toFixed(2)}</span>
              </div>
            </div>

            ${qrCodeDataUrl ? `
              <div class="ticket-qr">
                <img class="qr-code" src="${qrCodeDataUrl}" alt="Ticket QR Code">
              </div>
            ` : ''}

            <div class="validity-box">
              VALID UNTIL: ${validTill.toLocaleString('en-GB')}
            </div>

            <div class="copy-label">*** DUPLICATE - NOT ORIGINAL ***</div>

            <div class="ticket-footer">
              <div>Thank you for visiting!</div>
              <div style="margin-top: 2mm; font-size: 9px;">
                Cloudnet Softwares<br>
                +66-948712350 | +66-952477020
              </div>
            </div>
          </div>
        </div>
        
        <div class="no-print" style="text-align: center; margin-top: 10mm; padding: 5mm;">
          <button onclick="window.print()" style="
            background: linear-gradient(135deg, #007bff, #0056b3);
            color: white;
            border: none;
            padding: 10px 30px;
            border-radius: 8px;
            font-size: 16px;
            font-weight: 600;
            cursor: pointer;
            box-shadow: 0 4px 15px rgba(0, 123, 255, 0.3);
          ">
            🖨️ Print Combined Ticket (COPY)
          </button>
          <p style="margin-top: 10px; color: #666; font-size: 12px;">
            Ready to print combined ticket - REPRINT COPY
          </p>
        </div>
        <script>
          window.onafterprint = function() {
            setTimeout(function() {
              window.close();
            }, 500);
          };
        </script>
      </body>
      </html>
    `;

    printWindow.document.write(ticketHtml);
    printWindow.document.close();
    
    toast.success('Combined thermal print preview opened (COPY). Click print button.', {
      position: "top-right",
      autoClose: 3000,
    });
  };

  // ========== OLD HTML-BASED PRINT FUNCTIONS (DEPRECATED) ==========
  
  // Check authentication
  useEffect(() => {
    const userType = localStorage.getItem('usertype') || sessionStorage.getItem('usertype');
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    
    if (!userType || !token) {
      console.log('Missing authentication - userType:', userType, 'token:', !!token);
      toast.error('Please login to access this page');
      navigate('/login');
      return;
    }
    
    console.log('Authentication check passed - userType:', userType);
  }, [navigate]);

  // Load bills from database for the currently logged-in user only
  const loadBills = useCallback(async () => {
    try {
      setLoading(true);
      
      // Get current logged-in user ID from storage (same pattern as CashierTicketBilling)
      const currentUserId = localStorage.getItem('uname') || sessionStorage.getItem('uname') || 
                           localStorage.getItem('user_id') || sessionStorage.getItem('user_id') ||
                           localStorage.getItem('userid') || sessionStorage.getItem('userid');
      
      if (!currentUserId) {
        toast.error('User ID not found. Please login again.');
        navigate('/login');
        return;
      }

      console.log('Loading bills for logged-in user ID:', currentUserId); // Debug log
      console.log('User ID (parsed as int):', parseInt(currentUserId)); // Debug log
      
      // Storage debugging
      console.log('Storage check - uname:', localStorage.getItem('uname')); // Debug log
      console.log('Storage check - user_id:', localStorage.getItem('user_id')); // Debug log
      console.log('Storage check - userid:', localStorage.getItem('userid')); // Debug log
      
      // Authentication debugging
      console.log('Auth token check:', localStorage.getItem('token') || sessionStorage.getItem('token')); // Debug log
      console.log('User type check:', localStorage.getItem('usertype') || sessionStorage.getItem('usertype')); // Debug log

      // Load bills only for the current logged-in user using JOIN query
      let userBillsWithItems;
      
      try {
        // Parse user ID to integer (as stored in database cashier_id field)
        const userIdInt = parseInt(currentUserId);
        
        if (isNaN(userIdInt)) {
          console.log('Invalid user ID, cannot parse to integer:', currentUserId);
          toast.error('Invalid user ID format. Please login again.');
          navigate('/login');
          return;
        }
        
        console.log('Filtering bills for user cashier_id:', userIdInt); // Debug log
        
        // Primary filter: Load bills only (no JOIN) where cashier_id matches logged-in user
        try {
          userBillsWithItems = await fetchData("bills", null, "id",{'cashier_id': userIdInt});
         
          
          console.log('Bills found for user ID', userIdInt, ':', userBillsWithItems?.length || 0); // Debug log
        } catch (error) {
          console.error('Error fetching bills by cashier_id:', error);
          if (error.response?.status === 401) {
            toast.error('Authentication failed. Please login again.');
            navigate('/login');
            return;
          }
          userBillsWithItems = [];
        }
        
        // Fallback: Try filtering by cashier_name if cashier_id didn't work
        if ((!userBillsWithItems || userBillsWithItems.length === 0) && currentUserId) {
          console.log('No bills found by cashier_id, trying cashier_name for user:', currentUserId);
          try {
           userBillsWithItems =  await fetchData("bills", null, "id",{'cashier_id': userIdInt});
           
            console.log('Bills found with cashier_name filter for user:', userBillsWithItems?.length || 0); // Debug log
          } catch (error) {
            console.error('Error fetching bills by cashier_name:', error);
            if (error.response?.status === 401) {
              toast.error('Authentication failed. Please login again.');
              navigate('/login');
              return;
            }
            userBillsWithItems = [];
          }
        }
      } catch (error) {
        console.error('Error fetching bills for logged-in user:', error);
        toast.error('Failed to load your bills. Please try again.');
        // Security: Don't load any bills if we can't filter by user properly
        userBillsWithItems = [];
      }

      console.log('Total bills loaded for current user:', userBillsWithItems?.length || 0); // Debug log

      if (userBillsWithItems && Array.isArray(userBillsWithItems)) {
        // Debug: Check the structure of the first record
        if (userBillsWithItems.length > 0) {
          console.log('Sample bill record:', userBillsWithItems[0]);
          console.log('Bill record keys:', Object.keys(userBillsWithItems[0]));
        }
        
        // No need to group since we're loading bills directly, not JOIN results
        let filteredBills = userBillsWithItems;

        // Apply filters client-side since fetchData doesn't support complex filtering
        // Filter by date range
        if (filters.start_date && filters.end_date) {
          filteredBills = filteredBills.filter(bill => {
            const billDate = new Date(bill.created_at || bill.bill_date).toISOString().split('T')[0];
            return billDate >= filters.start_date && billDate <= filters.end_date;
          });
        }

        // Filter by payment method
        if (filters.payment_method) {
          filteredBills = filteredBills.filter(bill => 
            bill.payment_method === filters.payment_method
          );
        }

        // Filter by status
        if (filters.status) {
          if (filters.status === 'cancelled') {
            filteredBills = filteredBills.filter(bill => 
              bill.status === 'cancelled' || bill.status === 'refunded'
            );
          } else if (filters.status === 'active') {
            filteredBills = filteredBills.filter(bill => 
              bill.status === 'active' || !bill.status
            );
          }
        }

        // Calculate pagination
        const totalCount = filteredBills.length;
        const totalPages = Math.ceil(totalCount / filters.limit);
        const startIndex = (filters.page - 1) * filters.limit;
        const endIndex = startIndex + filters.limit;
        const paginatedBills = filteredBills.slice(startIndex, endIndex);

        setBills(paginatedBills);
        setPagination({
          total_count: totalCount,
          total_pages: totalPages,
          current_page: filters.page
        });
        
        toast.success(`Loaded ${paginatedBills.length} of your bills`);
      } else {
        console.warn('No bills found for current user');
        setBills([]);
        toast.info('No bills found for your account with the selected criteria');
      }
    } catch (error) {
      console.error('Error loading user bills:', error);
      toast.error('Failed to load your bills from database');
      setBills([]);
    } finally {
      setLoading(false);
    }
  }, [filters, navigate]); // filters are used for client-side filtering and pagination

  // Load bill details with items using separate queries
  const loadBillDetails = async (billId) => {
    try {
      setLoading(true);
      
      console.log('Loading bill details for ID:', billId); // Debug log
      
      // Load bill details first
      const billData =  await fetchData("bills", null, "id",{'id': billId });
     

      console.log('Bill data loaded:', billData); // Debug log

      if (!billData || billData.length === 0) {
        toast.error('Bill not found');
        return null;
      }

      const bill = billData[0]; // Get the first (and only) bill record
      
      // Load bill items separately
      const billItems = await fetchData("bill_items", null, "id", {'bill_id': billId });

      console.log('Bill items loaded:', billItems); // Debug log

      if (!billItems || billItems.length === 0) {
        toast.error('No items found for this bill');
        return null;
      }

      // Build the complete bill details object
      

      console.log('Bill items loaded:', billItems); // Debug log

      if (!billItems || billItems.length === 0) {
        toast.error('No items found for this bill');
        return null;
      }

      // Build the complete bill details object
      const billDetails = {
        id: bill.id,
        bill_number: bill.bill_number,
        customer_name: bill.customer_name,
        customer_phone: bill.customer_phone,
        cashier_name: bill.cashier_name,
        cashier_id: bill.cashier_id,
        payment_method: bill.payment_method,
        payment_status: bill.payment_status,
        total_amount: bill.total_amount,
        created_at: bill.created_at,
        notes: bill.notes,
        items: billItems
      };

      console.log('Processed bill details:', billDetails); // Debug log
      console.log('Number of items found:', billItems.length); // Debug log

      setBillDetails(billDetails);
      setShowDetailsModal(true);
      return billDetails;
    } catch (error) {
      console.error('Error loading bill details:', error);
      toast.error('Failed to load bill details');
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Handle print button click - load bill items separately for this specific bill
  const handlePrintClick = async (bill) => {
    try {
      console.log('Print button clicked for bill:', bill); // Debug log
      console.log('Bill ID being searched:', bill.id, 'Type:', typeof bill.id); // Debug log
      setCurrentBillForPrint(bill);
      
      // Load bill_items that belong to this specific bill
        const billItems = await fetchData("bill_items", null, "id", {'bill_id': bill.id });
     
      
      console.log('Loaded items for bill ID', bill.id, ':', billItems); // Debug log
      console.log('Number of items found:', billItems?.length || 0); // Debug log
      
      if (!billItems || billItems.length === 0) {
        toast.error('No items found for this bill');
        console.log('Bill object passed:', bill); // Debug log
        return;
      }
      
      // Build the bill details object with the loaded items
      const billDetails = {
        id: bill.id,
        bill_number: bill.bill_number,
        customer_name: bill.customer_name,
        customer_phone: bill.customer_phone,
        cashier_name: bill.cashier_name,
        cashier_id: bill.cashier_id,
        payment_method: bill.payment_method,
        payment_status: bill.payment_status,
        total_amount: bill.total_amount,
        created_at: bill.created_at,
        notes: bill.notes,
        items: billItems // Items loaded separately from bill_items table
      };
      
      console.log('Processed bill details with items:', billDetails); // Debug log
      console.log('Items for this bill:', billItems.length); // Debug log
      
      // Set the bill details for use in printing
      setBillDetails(billDetails);
      
      // Always show modal to let user choose print options (ESC/POS or HTML, Separate or Combined)
      console.log('Showing print modal for user to choose print options'); // Debug log
      setShowPrintModal(true);
    } catch (error) {
      console.error('Error loading items for bill:', error);
      toast.error('Failed to load items for this bill');
    }
  };

  // Generate tickets based on selected option - reload bill-specific items
  const generateTickets = async (billDetails, printType, printMethod = 'escpos') => {
    const bill = currentBillForPrint || billDetails;
    
    try {
      // Always reload fresh item data for the specific bill to ensure accuracy
      console.log('Reloading items for bill ID:', bill.id); // Debug log
      const freshBillItems = await fetchData("bill_items", null, "id", {'bill_id': bill.id });
     
      
      console.log('Fresh items loaded for bill ID', bill.id, ':', freshBillItems?.length || 0); // Debug log
      
      if (!freshBillItems || freshBillItems.length === 0) {
        toast.error('No items found for this bill');
        return;
      }
      
      console.log('Processing', freshBillItems.length, 'fresh items for bill', bill.bill_number); // Debug log
    
      if (printType === 'combined') {
        // Generate one combined ticket - use first item's ticket_id like separate tickets
        const ticketData = {
          ticketId: freshBillItems[0]?.ticket_id || `COMBINED-${bill.bill_number}`,
          originalBillNumber: bill.bill_number,
          bill_number: bill.bill_number,
          timestamp: bill.created_at,
          items: freshBillItems.map(item => ({
            item_name: item.item_name,
            quantity: item.quantity,
            total: item.total_price,
            ticket_id: item.ticket_id
          })),
          total: bill.total_amount,
          cashier: bill.cashier_name,
          customerName: bill.customer_name,
          paymentMethod: bill.payment_method,
          category: 'General Admission',
          isIndividual: false
        };
        
        if (printMethod === 'html') {
          await printCombinedTicketThermalHTML(ticketData);
        } else {
          await printTicket(ticketData);
        }
        toast.success(`Combined ticket for Bill ${bill.bill_number} sent to printer!`);
      } else {
        // Generate separate tickets
        const separateTickets = freshBillItems.map((item, index) => ({
          ticketId: item.ticket_id || `${bill.bill_number}-${index + 1}`,
          originalBillNumber: bill.bill_number,
          bill_number: bill.bill_number,
          timestamp: bill.created_at,
          items: [{
            item_name: item.item_name,
            quantity: item.quantity,
            total: item.total_price,
            ticket_id: item.ticket_id
          }],
          total: item.total_price,
          cashier: bill.cashier_name,
          customerName: bill.customer_name,
          paymentMethod: bill.payment_method,
          category: 'General Admission',
          isIndividual: true,
          itemNumber: index + 1,
          totalItems: freshBillItems.length,
          originalTicketId: item.ticket_id
        }));
        
        console.log('Generated separate tickets for bill', bill.bill_number, ':', separateTickets.map(t => ({ 
          ticketId: t.ticketId, 
          originalTicketId: t.originalTicketId 
        }))); // Debug log
        
        if (printMethod === 'html') {
          await printMultipleTicketsThermalHTML(separateTickets);
        } else {
          await printMultipleTickets(separateTickets);
        }
        toast.success(`${freshBillItems.length} separate tickets for Bill ${bill.bill_number} sent to printer!`);
      }
    } catch (error) {
      console.error('Error generating tickets for bill:', bill.id, error);
      toast.error('Failed to generate tickets. Please try again.');
      return;
    }
    
    // Close modals
    setShowPrintModal(false);
    setShowDetailsModal(false);
  };

  // OLD: Print multiple tickets function with boarding pass design (HTML-based)
  const printMultipleTickets_OLD = (ticketsArray) => {
    const printWindow = window.open('', '_blank');
    
    // Generate HTML for all tickets
    let allTicketsHtml = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Amusement Park Boarding Passes</title>
        <meta charset="UTF-8">
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;700&display=swap');
          
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          
          body { 
            font-family: 'Roboto', Arial, sans-serif; 
            background: white;
            padding: 0;
            margin: 0;
          }
          
          .tickets-container {
            gap: 0;
          }
          
          /* Fan-folded horizontal boarding pass - 215mm x 80mm */
          .boarding-pass {
            width: 215mm;
            height: 80mm;
            background: white;
            position: relative;
            overflow: hidden;
            font-size: 8px;
            page-break-after: always;
            page-break-inside: avoid;
            margin-bottom: 10mm;
            box-sizing: border-box;
          }
          
          .boarding-pass:last-child {
            page-break-after: auto;
            margin-bottom: 0;
          }
          
          /* Left section - Main ticket info */
          .main-section {
            width: 160mm;
            position: relative;
            float: left;
            height: 80mm;
            padding-left: 2mm;
          }
          
          
          /* Right section - Stub/Coupon */
          .stub-section {
            width: 55mm;
            position: relative;
            float: right;
            height: 80mm;
            overflow: hidden;
          }
          
          /* Fan-fold perforations */
          .stub-section::before {
            content: '';
            position: absolute;
            left: -1px;
            top: 0;
            bottom: 0;
            width: 2px;
          
          }
          
          .header-section {
            color: black;
            padding: 4mm 4mm;
            position: relative;
            height: 18mm;
            display: flex;
            align-items: center;
            justify-content: space-between;
          }
          
          .header-left {
            flex: 1;
          }
          
          .park-name {
            font-size: 14px;
            font-weight: 700;
            margin-bottom: 1mm;
            color: black;
          }
          
          .park-subtitle {
            font-size: 9px;
            opacity: 0.9;
            font-weight: 300;
          }
          
          .category-display {
            font-size: 14px;
            font-weight: 700;
            margin-top: 2mm;
            padding: 1mm 4mm;
            text-align: center;
            text-transform: uppercase;
            letter-spacing: 1px;
            color: black;
          }
          
          .header-right {
            text-align: right;
            font-size: 8px;
          }
          
          .ticket-id-display {
            font-family: 'Courier New', monospace;
            font-weight: 700;
            font-size: 9px;
          }
          
          .content-section {
            padding: 2mm 4mm;
            flex: 1;
            display: flex;
            gap: 3mm;
          }
          
          .left-content {
            flex: 1;
          }
          
          .right-content {
            width: 35mm;
          }
          
          .info-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 2mm 3mm;
            margin-bottom: 2mm;
          }
          
          .info-item {
            min-height: 6mm;
          }
          
          .info-label {
            font-size: 12px;
            font-weight: 700;
            color: #666;
            text-transform: uppercase;
            letter-spacing: 0.3px;
            margin-bottom: 0.5mm;
          }
          
          .info-value {
            font-size: 12px;
            font-weight: 600;
            color: #2c3e50;
            line-height: 1.1;
          }
          
          .items-compact {
            border-radius: 2mm;
            padding: 2mm;
            border-left: 1.5mm solid black;
            margin-bottom: 2mm;
          }
          
          .items-title {
            font-size: 9px;
            font-weight: 700;
            color: black;
            margin-bottom: 1mm;
            text-transform: uppercase;
          }
          
          .item-compact {
            display: flex;
            justify-content: space-between;
            font-size: 8px;
            margin-bottom: 0.5mm;
            padding: 0.3mm 0;
          }
          
          .item-compact:last-of-type {
            border-top: 1px solid black;
            padding-top: 1mm;
            margin-top: 1mm;
            font-weight: 700;
            color: black;
            font-size: 7px;
          }
          
          .qrcode-compact {
            text-align: center;
            background: white;
            
            padding: 1.5mm;
          }
          
          .qrcode-label {
            font-size: 10px;
            color: #666;
            margin-bottom: 0.5mm;
            text-transform: uppercase;
            font-weight: 600;
          }
          
          /* Ferris Wheel Section Styling */
          .ferris-wheel-section {
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
          }
          
          .ferris-wheel-container {
            text-align: center;
            padding: 3mm;
            background: linear-gradient(135deg, #ff6b6b, #4ecdc4);
            border-radius: 5mm;
            color: white;
            box-shadow: 0 3mm 6mm rgba(0,0,0,0.3);
            display: flex;
            align-items: center;
            justify-content: center;
          }
          
          .ferris-wheel {
            font-size: 48px;
            animation: rotate 3s linear infinite;
          }
          
          @keyframes rotate {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          
          /* Stub section styling */
          .stub-header {
            color: black;
            padding: 4mm;
            text-align: center;
            height: auto;
            display: flex;
            flex-direction: column;
            justify-content: center;
            margin-bottom: 2mm;
          }
          
          .stub-title {
            font-size: 15px;
            font-weight: 700;
            margin-bottom: 0.5mm;
          }
          
          .stub-subtitle {
            font-size: 13px;
            opacity: 0.8;
          }
          
          .stub-content {
            padding: 2mm;
            flex: 1;
            display: flex;
            flex-direction: column;
          }
          
          .stub-info-grid {
            display: grid;
            grid-template-columns: 1fr;
            gap: 0.5mm;
            margin-bottom: 2mm;
          }
          
          .stub-info-item {
            min-height: 8mm;
            display: flex;
            flex-direction: column;
            justify-content: center;
            padding: 1mm;
            border-bottom: 1px solid #eee;
          }
          
          .stub-info-item:last-child {
            border-bottom: none;
          }
          
          .stub-info {
            margin-bottom: 2mm;
          }
          
          .stub-label {
            font-size: 10px;
            font-weight: 700;
            color: #666;
            text-transform: uppercase;
            margin-bottom: 0.5mm;
          }
          
          .stub-value {
            font-size: 11px;
            font-weight: 600;
            color: #2c3e50;
          }
          
          .validity-compact {
            background: #fff3cd;
            border: 1px solid #ffc107;
            border-radius: 2mm;
            padding: 2mm;
            text-align: center;
            margin: 2mm 0;
          }
          
          .validity-text {
            color: #856404;
            font-weight: 600;
            font-size: 7px;
            line-height: 1.3;
          }
          
          .footer-compact {
            background: #FFFFFFFF;
            color: black;
            padding: 0mm;
            text-align: center;
            font-size: 11px;
            line-height: 1.3;
            opacity: 0.9;
          }
          

          
          /* Print page setup for multiple tickets */
          @page {
            size: 215mm 80mm;
            margin: 0;
          }
          
          /* Print styles for multiple tickets */
          @media print { 
            * {
              -webkit-print-color-adjust: exact !important;
              color-adjust: exact !important;
            }
            
            .no-print { display: none; }
            
            html, body { 
              background: white; 
              padding: 0; 
              margin: 0;
              width: auto;
              height: auto;
              overflow: visible;
            }
            
            .tickets-container {
              width: auto;
              height: auto;
            }
            
            .boarding-pass {
              width: 215mm !important;
              height: 80mm !important;
              box-shadow: none;
              page-break-after: always !important;
              page-break-inside: avoid !important;
              margin: 0 !important;
              padding: 0 !important;
              transform: scale(1) !important;
              display: block !important;
            }
            
            .boarding-pass:last-child {
              page-break-after: auto !important;
            }
            
            .main-section {
              width: 160mm !important;
              height: 80mm !important;
            }
            
            .stub-section {
              width: 55mm !important;
              height: 80mm !important;
            }
          }
        </style>
      </head>
      <body>
        <div class="tickets-container">
    `;

    // Add each ticket to the HTML
    ticketsArray.forEach((ticketData) => {
      const currentDate = new Date(ticketData.timestamp);
      const validTill = new Date(currentDate.getTime() + (1 * 60 * 60 * 1000)); // 1 hour validity
      
      allTicketsHtml += generateSingleTicketHTML_OLD(ticketData, currentDate, validTill);
    });

    // Close the container and add print button
    allTicketsHtml += `
        </div>
        
        <div class="no-print" style="text-align: center; margin-top: 30px; position: fixed; bottom: 20px; left: 50%; transform: translateX(-50%); background: white; padding: 20px; border-radius: 10px; box-shadow: 0 4px 20px rgba(0,0,0,0.1);">
          <p style="margin-bottom: 15px; color: #666; font-size: 1rem;">
            <strong>${ticketsArray.length} Individual Tickets Ready</strong><br>
            <small>Each ticket can be used separately for entry</small>
          </p>
          <button onclick="window.print()" style="
            background: linear-gradient(135deg, #d90429, #b8032a);
            color: white;
            border: none;
            padding: 15px 40px;
            border-radius: 25px;
            font-size: 1.1rem;
            font-weight: 600;
            cursor: pointer;
            margin: 0 10px;
            box-shadow: 0 4px 15px rgba(217, 4, 41, 0.3);
          ">🖨️ Print All ${ticketsArray.length} Tickets</button>
          <button onclick="window.close()" style="
            background: linear-gradient(135deg, #6c757d, #495057);
            color: white;
            border: none;
            padding: 15px 30px;
            border-radius: 25px;
            font-size: 1rem;
            font-weight: 600;
            cursor: pointer;
            margin: 0 10px;
            box-shadow: 0 4px 15px rgba(108, 117, 125, 0.3);
          ">✕ Close</button>
        </div>
      </body>
      </html>
    `;

    printWindow.document.write(allTicketsHtml);
    printWindow.document.close();
  };

  // OLD: Helper function to generate HTML for a single ticket
  const generateSingleTicketHTML_OLD = (ticketData, currentDate, validTill) => {
    return `
      <div class="boarding-pass">
        <!-- Main Section (Left) -->
        <div class="main-section">
          <div class="header-section">
            <div class="header-left">
              <div class="park-name">🎡 The View Pattaya</div>
              <div class="park-subtitle">Boarding Pass • Entry Ticket${ticketData.isIndividual ? ` • Individual Ticket ${ticketData.itemNumber}/${ticketData.totalItems}` : ''}</div>
            </div>
            <div class="header-right">
              <div class="ticket-id-display">${ticketData.ticketId}</div>
              ${ticketData.isIndividual ? `<div style="font-size: 8px; margin-top: 2mm; color: rgba(255,255,255,0.8);">Item: ${ticketData.items[0].item_name}</div>` : ''}
            </div>
          </div>
          
          <div class="content-section">
            <div class="left-content">
              <div class="info-grid">
                <div class="info-item">
                  <div class="info-label">Issue Date</div>
                  <div class="info-value">${currentDate.toLocaleDateString()}</div>
                </div>
                <div class="info-item">
                  <div class="info-label">Issue Time</div>
                  <div class="info-value">${currentDate.toLocaleTimeString()}</div>
                </div>
                <div class="info-item">
                  <div class="info-label">Valid Until</div>
                  <div class="info-value">${validTill.toLocaleTimeString()}</div>
                </div>
                <div class="info-item">
                  <div class="info-label">Payment Method</div>
                  <div class="info-value">${ticketData.paymentMethod}</div>
                </div>
              </div>
              
              <div class="items-compact">
                <div class="items-title">� Attractions & Services</div>
                ${ticketData.items.map(item => `
                  <div class="item-compact">
                    <span>${item.item_name} × ${item.quantity}</span>
                    <span>฿${(typeof item.total === 'number' ? item.total : parseFloat(item.total) || 0).toFixed(2)}</span>
                  </div>
                `).join('')}
                <div class="item-compact">
                  <span>TICKET AMOUNT</span>
                  <span>฿${ticketData.total}</span>
                </div>
              </div>
              
              <div class="validity-compact">
                <div class="validity-text">
                  ⏰ Valid for 1 HOUR • Present at each attraction
                </div>
              </div>
            </div>
            
            <div class="right-content">
              <div class="ferris-wheel-section">
                <div class="ferris-wheel-container">
                  <div class="ferris-wheel">
                    🎡
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div class="footer-compact">
            🎢 The View Pattaya • Keep with you at all times • Non-transferable • No refunds
          </div>
        </div>
        
      
        
        <!-- Stub Section (Right) -->
        <div class="stub-section">
          <div class="stub-header">
            <div class="stub-title">${ticketData.isIndividual ? 'INDIVIDUAL TICKET' : 'ENTRY STUB'}</div>
            <div class="stub-subtitle">${ticketData.isIndividual ? `Ticket ${ticketData.itemNumber}/${ticketData.totalItems}` : 'Keep This Portion'}</div>
          </div>
          
          <div class="stub-content">
            <div class="stub-info-grid">
              <div class="stub-info-item">
                <div class="stub-label">Ticket ID</div>
                <div class="stub-value">${ticketData.ticketId}</div>
              </div>
              
              <div class="stub-info-item">
                <div class="stub-label">Date & Time</div>
                <div class="stub-value">${currentDate.toLocaleDateString()}<br>${currentDate.toLocaleTimeString()}</div>
              </div>
              
              <div class="stub-info-item">
                <div class="stub-label">Total Amount</div>
                <div class="stub-value">฿${ticketData.total}</div>
              </div>
              
              <div class="stub-info-item">
                <div class="stub-label">Category</div>
                <div class="stub-value">${ticketData.category || 'General Admission'}</div>
              </div>
              
              <div class="stub-info-item">
                <div class="stub-label">Cashier</div>
                <div class="stub-value">ID:${ticketData.cashier}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  };

  // OLD: Print ticket function (HTML-based, now calls printMultipleTickets_OLD for consistency)
  const printTicket_OLD = (ticketData) => {
    // Convert single ticket to array format for consistency
    printMultipleTickets_OLD([ticketData]);
  };

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1 // Reset to first page when filters change
    }));
  };

  // Handle pagination
  const handlePageChange = (newPage) => {
    setFilters(prev => ({
      ...prev,
      page: newPage
    }));
  };

  // Load bills on component mount and filter changes
  useEffect(() => {
    loadBills();
  }, [loadBills]);

  return (
    <div className="bills-management-container" style={{ 
      minHeight: '100vh', 
      backgroundColor: '#f8f9fa',
      fontFamily: 'Arial, sans-serif'
    }}>
      {/* Back to POS Button */}
      <button
        className="btn btn-primary btn-sm shadow"
        onClick={() => navigate('/pos/ticketbilling')}
        style={{ 
          position: 'fixed', 
          top: '20px', 
          left: '20px', 
          zIndex: 1000,
          borderRadius: '25px',
          padding: '8px 16px',
          fontSize: '16px',
          fontWeight: '600',
          border: 'none',
          background: 'linear-gradient(45deg, #007bff, #0056b3)',
          transition: 'all 0.3s ease'
        }}
        onMouseOver={(e) => {
          e.target.style.transform = 'translateY(-2px)';
          e.target.style.boxShadow = '0 8px 25px rgba(0,123,255,0.4)';
        }}
        onMouseOut={(e) => {
          e.target.style.transform = 'translateY(0)';
          e.target.style.boxShadow = '0 4px 15px rgba(0,0,0,0.2)';
        }}
      >
        <i className="fas fa-arrow-left me-2"></i>
        Back to POS
      </button>
      
      <div className="container-fluid" style={{ 
        paddingTop: '100px', 
        paddingBottom: '50px',
        paddingLeft: '20px',
        paddingRight: '20px'
      }}>
        <div className="row justify-content-center">
          <div className="col-12">
            <div className="card shadow-lg border-0 rounded-lg" style={{ 
              borderRadius: '15px',
              overflow: 'hidden'
            }}>
              <div className="card-header text-white" style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                padding: '20px 30px'
              }}>
                <h3 className="card-title mb-0" style={{ fontSize: '24px', fontWeight: '700' }}>
                  <i className="fas fa-receipt me-3"></i>
                  Bills Management & Ticket Reprinting
                </h3>
              </div>
              
              <div className="card-body" style={{ padding: '30px' }}>
                {/* Filters */}
                <div className="row mb-4">
                  <div className="col-md-3">
                    <label className="form-label">Start Date:</label>
                    <input
                      type="date"
                      className="form-control"
                      value={filters.start_date}
                      onChange={(e) => handleFilterChange('start_date', e.target.value)}
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">End Date:</label>
                    <input
                      type="date"
                      className="form-control"
                      value={filters.end_date}
                      onChange={(e) => handleFilterChange('end_date', e.target.value)}
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Payment Method:</label>
                    <select
                      className="form-control"
                      value={filters.payment_method}
                      onChange={(e) => handleFilterChange('payment_method', e.target.value)}
                    >
                      <option value="">All Methods</option>
                      <option value="cash">Cash</option>
                      <option value="qr">QR Code</option>
                      <option value="card">Card</option>
                      <option value="upi">UPI</option>
                      <option value="online">Online</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Bill Status:</label>
                    <select
                      className="form-control"
                      value={filters.status}
                      onChange={(e) => handleFilterChange('status', e.target.value)}
                    >
                      <option value="">All Bills</option>
                      <option value="active">Active Bills</option>
                      <option value="cancelled">Cancelled Bills</option>
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Items per page:</label>
                    <select
                      className="form-control"
                      value={filters.limit}
                      onChange={(e) => handleFilterChange('limit', parseInt(e.target.value))}
                    >
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>
                </div>

                {/* Loading indicator */}
                {loading && (
                  <div className="text-center my-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2">Loading bills...</p>
                  </div>
                )}

                {/* Bills Table with Vertical Scroll */}
                {!loading && (
                  <>
                    <div className="table-responsive" style={{
                      maxHeight: '600px', 
                      overflowY: 'auto',
                      border: '1px solid #dee2e6',
                      borderRadius: '8px'
                    }}>
                      <table className="table table-hover table-striped mb-0">
                        <thead className="table-dark" style={{ 
                          position: 'sticky', 
                          top: 0, 
                          zIndex: 10 
                        }}>
                          <tr>
                            <th>Bill Number</th>
                            <th>Date & Time</th>
                            <th>Customer</th>
                            <th>Cashier</th>
                            <th>Payment Method</th>
                            <th>Total Amount</th>
                            <th>Status</th>
                            <th>Notes</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {bills.length === 0 ? (
                            <tr>
                              <td colSpan="9" className="text-center py-4">
                                <i className="fas fa-receipt fa-3x text-muted mb-3"></i>
                                <p className="text-muted">No bills found for the selected criteria</p>
                              </td>
                            </tr>
                          ) : (
                            bills.map((bill) => (
                              <tr 
                                key={bill.id}
                                style={{
                                  backgroundColor: bill.status === 'cancelled' || bill.status === 'refunded' 
                                    ? '#ffe6e6' 
                                    : 'transparent'
                                }}
                              >
                                <td>
                                  <strong className="text-primary">{bill.bill_number}</strong>
                                </td>
                                <td>
                                  <div>{new Date(bill.created_at).toLocaleDateString()}</div>
                                  <small className="text-muted">
                                    {new Date(bill.created_at).toLocaleTimeString()}
                                  </small>
                                </td>
                                <td>
                                  <div>{bill.customer_name || 'Walk-in Customer'}</div>
                                  {bill.customer_phone && (
                                    <small className="text-muted">{bill.customer_phone}</small>
                                  )}
                                </td>
                                <td>{bill.cashier_name || 'Unknown'}</td>
                                <td>
                                  <span className={`badge ${
                                    bill.payment_method === 'cash' ? 'bg-success' :
                                    bill.payment_method === 'qr' ? 'bg-primary' :
                                    bill.payment_method === 'card' ? 'bg-warning' :
                                    'bg-secondary'
                                  }`}>
                                    {bill.payment_method?.toUpperCase()}
                                  </span>
                                </td>
                                <td>
                                  <strong className="text-success">
                                    ฿{parseFloat(bill.total_amount).toFixed(2)}
                                  </strong>
                                </td>
                                <td>
                                  <span className={`badge ${
                                    bill.status === 'cancelled' ? 'bg-danger' :
                                    bill.status === 'refunded' ? 'bg-warning' :
                                    'bg-success'
                                  }`}>
                                    {bill.status?.toUpperCase() || 'ACTIVE'}
                                  </span>
                                </td>
                                <td>
                                  {bill.notes ? (
                                    <small 
                                      className="text-muted" 
                                      style={{ 
                                        maxWidth: '200px', 
                                        display: 'inline-block',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                      }}
                                      title={bill.notes}
                                    >
                                      {bill.notes}
                                    </small>
                                  ) : (
                                    <small className="text-muted">-</small>
                                  )}
                                </td>
                                <td>
                                  <div className="btn-group" role="group">
                                    <button
                                      className="btn btn-sm btn-outline-info"
                                      onClick={() => loadBillDetails(bill.id)}
                                      title="View Details"
                                    >
                                      <i className="fas fa-eye"></i>
                                    </button>
                                    <button
                                      className="btn btn-sm btn-outline-success"
                                      onClick={() => showBillQRModal(bill)}
                                      title="Show QR Code"
                                    >
                                      <i className="fas fa-qrcode"></i>
                                    </button>
                                    <button
                                      className="btn btn-sm btn-outline-primary"
                                      onClick={() => handlePrintClick(bill)}
                                      title="Print Tickets"
                                    >
                                      <i className="fas fa-print"></i>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination */}
                    {pagination.total_pages > 1 && (
                      <nav aria-label="Bills pagination">
                        <ul className="pagination justify-content-center">
                          <li className={`page-item ${pagination.current_page === 1 ? 'disabled' : ''}`}>
                            <button
                              className="page-link"
                              onClick={() => handlePageChange(pagination.current_page - 1)}
                              disabled={pagination.current_page === 1}
                            >
                              Previous
                            </button>
                          </li>
                          
                          {[...Array(pagination.total_pages)].map((_, index) => {
                            const page = index + 1;
                            if (
                              page === 1 ||
                              page === pagination.total_pages ||
                              (page >= pagination.current_page - 2 && page <= pagination.current_page + 2)
                            ) {
                              return (
                                <li
                                  key={page}
                                  className={`page-item ${pagination.current_page === page ? 'active' : ''}`}
                                >
                                  <button
                                    className="page-link"
                                    onClick={() => handlePageChange(page)}
                                  >
                                    {page}
                                  </button>
                                </li>
                              );
                            } else if (
                              page === pagination.current_page - 3 ||
                              page === pagination.current_page + 3
                            ) {
                              return (
                                <li key={page} className="page-item disabled">
                                  <span className="page-link">...</span>
                                </li>
                              );
                            }
                            return null;
                          })}
                          
                          <li className={`page-item ${pagination.current_page === pagination.total_pages ? 'disabled' : ''}`}>
                            <button
                              className="page-link"
                              onClick={() => handlePageChange(pagination.current_page + 1)}
                              disabled={pagination.current_page === pagination.total_pages}
                            >
                              Next
                            </button>
                          </li>
                        </ul>
                        
                        <div className="text-center mt-2">
                          <small className="text-muted">
                            Showing page {pagination.current_page} of {pagination.total_pages} 
                            ({pagination.total_count} total bills)
                          </small>
                        </div>
                      </nav>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bill Details Modal */}
      {showDetailsModal && billDetails && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fas fa-receipt me-2"></i>
                  Bill Details - {billDetails.bill_number}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowDetailsModal(false)}
                ></button>
              </div>
              
              <div className="modal-body">
                <div className="row mb-3">
                  <div className="col-md-6">
                    <strong>Customer:</strong> {billDetails.customer_name || 'Walk-in Customer'}
                    {billDetails.customer_phone && (
                      <><br /><small>Phone: {billDetails.customer_phone}</small></>
                    )}
                  </div>
                  <div className="col-md-6">
                    <strong>Cashier:</strong> {billDetails.cashier_name || 'Unknown'}
                    <br />
                    <strong>Date:</strong> {new Date(billDetails.created_at).toLocaleString()}
                  </div>
                </div>
                
                <div className="row mb-3">
                  <div className="col-md-6">
                    <strong>Payment Method:</strong> 
                    <span className={`badge ms-2 ${
                      billDetails.payment_method === 'cash' ? 'bg-success' :
                      billDetails.payment_method === 'qr' ? 'bg-primary' :
                      billDetails.payment_method === 'card' ? 'bg-warning' :
                      'bg-secondary'
                    }`}>
                      {billDetails.payment_method?.toUpperCase()}
                    </span>
                  </div>
                  <div className="col-md-6">
                    <strong>Status:</strong> 
                    <span className="badge bg-success ms-2">{billDetails.payment_status?.toUpperCase()}</span>
                  </div>
                </div>

                <h6>Items:</h6>
                <div className="table-responsive">
                  <table className="table table-sm table-striped">
                    <thead>
                      <tr>
                        <th>Item Name</th>
                        <th>Category</th>
                        <th>Unit Price</th>
                        <th>Quantity</th>
                        <th>Total Price</th>
                      </tr>
                    </thead>
                    <tbody>
                      {billDetails.items.map((item, index) => (
                        <tr key={index}>
                          <td>{item.item_name}</td>
                          <td>{item.category_name || 'N/A'}</td>
                          <td>฿{parseFloat(item.unit_price).toFixed(2)}</td>
                          <td>{item.quantity}</td>
                          <td>฿{parseFloat(item.total_price).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <th colSpan="4">Total Amount:</th>
                        <th>฿{parseFloat(billDetails.total_amount).toFixed(2)}</th>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                {billDetails.notes && (
                  <div className="mt-3">
                    <strong>Notes:</strong>
                    <p className="text-muted">{billDetails.notes}</p>
                  </div>
                )}
              </div>
              
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowDetailsModal(false)}
                >
                  Close
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => {
                    setShowDetailsModal(false);
                    handlePrintClick(billDetails);
                  }}
                >
                  <i className="fas fa-print me-2"></i>
                  Print Tickets
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Ticket Printing Options Modal */}
      {showPrintModal && currentBillForPrint && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-xl" style={{ maxWidth: '900px' }}>
            <div className="modal-content" style={{ borderRadius: '15px', overflow: 'hidden' }}>
              <div className="modal-header text-center" style={{ 
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: 'none',
                padding: '20px'
              }}>
                <h5 className="modal-title w-100" style={{ fontWeight: '700', fontSize: '18px' }}>
                  🎫 Ticket Reprint Options (COPY)
                </h5>
              </div>
              
              <div className="modal-body text-center" style={{ padding: '40px 30px' }}>
                <p style={{ color: '#6c757d', marginBottom: '10px', fontSize: '16px', fontWeight: '600' }}>
                  Choose ticket format and print method:
                </p>
                <p style={{ color: '#dc3545', marginBottom: '30px', fontSize: '13px', fontWeight: '500' }}>
                  ⚠️ All reprinted tickets will be marked as "COPY"
                </p>
                
                {/* First Row - ESC/POS Thermal */}
                <div className="row g-3 mb-3">
                  <div className="col-12">
                    <h6 style={{ textAlign: 'left', color: '#495057', fontWeight: '600', marginBottom: '10px' }}>
                      🖨️ ESC/POS Thermal Printer
                    </h6>
                  </div>
                  {/* Combined - ESC/POS */}
                  <div className="col-md-6">
                    <button
                      className="btn btn-danger w-100"
                      onClick={() => generateTickets(billDetails, 'combined', 'escpos')}
                      style={{
                        borderRadius: '10px',
                        padding: '15px 10px',
                        fontSize: '13px',
                        fontWeight: '600',
                        border: 'none',
                        background: 'linear-gradient(45deg, #dc3545, #c82333)',
                        boxShadow: '0 4px 15px rgba(220,53,69,0.3)',
                        transition: 'all 0.3s ease',
                        minHeight: '130px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        lineHeight: '1.3'
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 8px 20px rgba(220,53,69,0.4)';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 4px 15px rgba(220,53,69,0.3)';
                      }}
                    >
                      <div style={{ fontSize: '28px', marginBottom: '6px' }}>📋</div>
                      <div style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '3px' }}>Combined</div>
                      <div style={{ opacity: 0.9, fontSize: '11px', marginBottom: '4px' }}>ESC/POS Thermal</div>
                      <div style={{ opacity: 0.85, fontSize: '10px' }}>All items in one</div>
                    </button>
                  </div>
                  
                  {/* Separate - ESC/POS */}
                  <div className="col-md-6">
                    <button
                      className="btn btn-success w-100"
                      onClick={() => generateTickets(billDetails, 'separate', 'escpos')}
                      style={{
                        borderRadius: '10px',
                        padding: '15px 10px',
                        fontSize: '13px',
                        fontWeight: '600',
                        border: 'none',
                        background: 'linear-gradient(45deg, #28a745, #1e7e34)',
                        boxShadow: '0 4px 15px rgba(40,167,69,0.3)',
                        transition: 'all 0.3s ease',
                        minHeight: '130px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        lineHeight: '1.3'
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 8px 20px rgba(40,167,69,0.4)';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 4px 15px rgba(40,167,69,0.3)';
                      }}
                    >
                      <div style={{ fontSize: '28px', marginBottom: '6px' }}>🎟️</div>
                      <div style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '3px' }}>Separate</div>
                      <div style={{ opacity: 0.9, fontSize: '11px', marginBottom: '4px' }}>ESC/POS Thermal</div>
                      <div style={{ opacity: 0.85, fontSize: '10px' }}>One per item</div>
                    </button>
                  </div>
                </div>
                
                {/* Second Row - HTML Thermal */}
                <div className="row g-3">
                  <div className="col-12">
                    <h6 style={{ textAlign: 'left', color: '#495057', fontWeight: '600', marginBottom: '10px' }}>
                      🌐 HTML Thermal (Browser)
                    </h6>
                  </div>
                  {/* Combined - HTML */}
                  <div className="col-md-6">
                    <button
                      className="btn btn-warning w-100"
                      onClick={() => generateTickets(billDetails, 'combined', 'html')}
                      style={{
                        borderRadius: '10px',
                        padding: '15px 10px',
                        fontSize: '13px',
                        fontWeight: '600',
                        border: 'none',
                        background: 'linear-gradient(45deg, #ffc107, #e0a800)',
                        boxShadow: '0 4px 15px rgba(255,193,7,0.3)',
                        transition: 'all 0.3s ease',
                        minHeight: '130px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        lineHeight: '1.3',
                        color: '#000'
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 8px 20px rgba(255,193,7,0.4)';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 4px 15px rgba(255,193,7,0.3)';
                      }}
                    >
                      <div style={{ fontSize: '28px', marginBottom: '6px' }}>📋</div>
                      <div style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '3px' }}>Combined</div>
                      <div style={{ opacity: 0.9, fontSize: '11px', marginBottom: '4px' }}>HTML Thermal</div>
                      <div style={{ opacity: 0.85, fontSize: '10px' }}>Browser print</div>
                    </button>
                  </div>
                  
                  {/* Separate - HTML */}
                  <div className="col-md-6">
                    <button
                      className="btn btn-info w-100"
                      onClick={() => generateTickets(billDetails, 'separate', 'html')}
                      style={{
                        borderRadius: '10px',
                        padding: '15px 10px',
                        fontSize: '13px',
                        fontWeight: '600',
                        border: 'none',
                        background: 'linear-gradient(45deg, #17a2b8, #138496)',
                        boxShadow: '0 4px 15px rgba(23,162,184,0.3)',
                        transition: 'all 0.3s ease',
                        minHeight: '130px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        lineHeight: '1.3',
                        color: '#fff'
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 8px 20px rgba(23,162,184,0.4)';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 4px 15px rgba(23,162,184,0.3)';
                      }}
                    >
                      <div style={{ fontSize: '28px', marginBottom: '6px' }}>🎟️</div>
                      <div style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '3px' }}>Separate</div>
                      <div style={{ opacity: 0.9, fontSize: '11px', marginBottom: '4px' }}>HTML Thermal</div>
                      <div style={{ opacity: 0.85, fontSize: '10px' }}>Browser print</div>
                    </button>
                  </div>
                </div>
                
                <div className="mt-4">
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => setShowPrintModal(false)}
                    style={{ 
                      borderRadius: '8px', 
                      padding: '8px 20px',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                  >
                    <i className="fas fa-times me-2"></i>
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* QR Code Modal */}
      {showQRModal && qrData && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 10000,
          fontFamily: 'Roboto, Arial, sans-serif',
        }}>
          <div style={{
            background: 'white',
            padding: '40px',
            borderRadius: '20px',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
            maxWidth: '500px',
            width: '90%',
            textAlign: 'center',
            position: 'relative',
          }}>
            <div style={{
              position: 'absolute',
              top: '15px',
              right: '20px',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#999',
              fontWeight: 'bold',
            }} onClick={closeQRModal}>×</div>

            <h2 style={{
              color: '#667eea',
              marginBottom: '10px',
              fontSize: '1.8rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
            }}>
              <span>📱 Bill QR Code</span>
            </h2>

            <p style={{
              marginBottom: '20px',
              color: '#666',
              fontSize: '1rem',
            }}>
              Scan to view bill details
            </p>

            <div style={{
              background: '#f8f9fa',
              padding: '20px',
              borderRadius: '15px',
              margin: '20px 0',
              border: '2px solid #667eea',
            }}>
              <div style={{
                width: '250px',
                height: '250px',
                margin: '0 auto 15px',
                background: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid #ddd',
                borderRadius: '10px',
              }}>
                <QRCode
                  value={qrData.billNumber || 'N/A'}
                  size={220}
                  level="M"
                  includeMargin={true}
                />
              </div>

              <div style={{
                fontSize: '1.1rem',
                fontWeight: 'bold',
                color: '#667eea',
                marginBottom: '10px',
              }}>Bill #{qrData.billNumber}</div>

              <div style={{
                fontSize: '1rem',
                color: '#666',
                marginBottom: '5px',
              }}>
                Amount: ฿{parseFloat(qrData.amount).toFixed(2)}
              </div>

              {qrData.customerName && (
                <div style={{
                  fontSize: '0.9rem',
                  color: '#999',
                }}>
                  Customer: {qrData.customerName}
                </div>
              )}

              {qrData.createdAt && (
                <div style={{
                  fontSize: '0.9rem',
                  color: '#999',
                  marginTop: '10px',
                }}>
                  Date: {new Date(qrData.createdAt).toLocaleDateString()} {new Date(qrData.createdAt).toLocaleTimeString()}
                </div>
              )}
            </div>

            <button
              onClick={closeQRModal}
              style={{
                background: '#667eea',
                color: 'white',
                border: 'none',
                padding: '10px 30px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: '600',
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );
}